import { Component, inject } from '@angular/core';
import { NgFor } from '@angular/common';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Asset } from '../../interfaces/Asset';
import { Brand } from '../../interfaces/Brand';
import { BranchOffice } from '../../interfaces/BranchOffice';
import { BrandResponse } from '../../interfaces/BrandResponse';
import { BranchOfficeResponse } from '../../interfaces/BranchOfficeResponse';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { AssetVerificationStatus } from '../../interfaces/AssetVerificationStatus';
import { AssetVerificationStatusResponse } from '../../interfaces/AssetVerificationStatusResponse';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-create-asset',
    standalone: true,
    imports: [
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgFor,
    ],
    templateUrl: './m-create-asset.component.html',
    styleUrl: './m-create-asset.component.scss',
})
export class MCreateAssetComponent {
    // isToggled
    isToggled = false;

    // Form
    addAssetForm: FormGroup;

    // Show alert
    showAlert: boolean = false;

    // Array of locations
    locations: string[] = ['internal', 'external'];

    // Array of type of assets
    types: string[] = [
        'url',
        'server',
        'ip',
        'domain',
        'sub domain',
        'applicacion',
        'others',
    ];

    // Array of status
    statusList: AssetVerificationStatus[] = [];

    selectedStatus: AssetVerificationStatus | undefined;

    // Array of companies
    companies: Company[] = [];

    selectedCompany: string = '';

    // Array of brands
    brands: Brand[] = [];

    selectedBrand: Brand | undefined;

    // Array of branch offices
    branchOffices: BranchOffice[] = [];

    selectedBranchOffice: BranchOffice | undefined;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router
    ) {
        this.addAssetForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(200)]],
            location: ['', [Validators.maxLength(60)]],
            operatingSystem: ['', [Validators.maxLength(60)]],
            evaluable: [false, []],
            assetType: ['', [Validators.maxLength(60)]],
            brand: [this.selectedBrand, [Validators.required]],
            branchOffice: [this.selectedBranchOffice, []],
            assetVerificationStatus: [
                this.selectedStatus,
                [Validators.required],
            ],
            ip: [
                '',
                [
                    Validators.maxLength(40),
                    Validators.pattern(
                        '(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)'
                    ),
                ],
            ],
            port: ['', [Validators.min(1), Validators.max(65535)]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.apiService
            .getAssetVerificationStatus()
            .subscribe((response: AssetVerificationStatusResponse) => {
                this.statusList.push(...response.assetVerificationStatuses);
            });
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
            });
    }

    private filterBrand(value: string): Brand[] {
        const filterValue = value.toLowerCase();
        return this.brands.filter((brand) =>
            brand.name.toLowerCase().includes(filterValue)
        );
    }

    private filterBranchOffice(value: string): BranchOffice[] {
        const filterValue = value.toLowerCase();
        return this.branchOffices.filter((branchOffice) =>
            branchOffice.name.toLowerCase().includes(filterValue)
        );
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Change company
    changeCompany(companyId: string) {
        this.apiService
            .getBrandByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BrandResponse) => {
                this.brands = [];
                if (response.brands.length > 0) {
                    this.brands.push(...response.brands);
                }
            });
        this.apiService
            .getBranchOfficeByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BranchOfficeResponse) => {
                this.branchOffices = [];
                if (response.branchOffices.length > 0) {
                    this.branchOffices.push(...response.branchOffices);
                }
            });
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.addAssetForm.invalid) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.addAssetForm.disable();

        // Hide the alert
        this.showAlert = false;

        // Add Asset
        let newAsset: Asset = { ...this.addAssetForm.value };
        this.apiService.addAsset(newAsset).subscribe({
            next: (value) => {
                console.info(value);
                // Re-enable the form
                this.addAssetForm.enable();

                // Show the alert
                this.showAlert = true;
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
                // Re-enable the form
                this.addAssetForm.enable();

                // Show the alert
                this.showAlert = true;
            },
            complete: () => {
                this.router.navigateByUrl('/masters/assets');
            },
        });
    }
}
